import { Controller } from "@hotwired/stimulus"

let pendingHistoryRestoration = null

export default class extends Controller {
  static targets = [
    "form",
    "name",
    "textarea",
    "editPanel",
    "previewPanel",
    "previewContent",
    "editButton",
    "previewButton",
    "dirtyStatus"
  ]

  static values = {
    previewUrl: String
  }

  connect() {
    this.originalName = this.nameTarget.value
    this.originalContent = this.textareaTarget.value
    this.previewContent = this.textareaTarget.value
    this.previewRequest = 0
    this.submitting = false
    this.pendingNavigationUrl = null
    this.pendingNavigationReset = null
    this.editorUrl = window.location.href
    this.historyIndex = window.history.state?.turbo?.restorationIndex
    this.restoringHistory = false
    this.boundBeforeVisit = this.beforeVisit.bind(this)
    this.boundBeforeUnload = this.beforeUnload.bind(this)
    this.boundPopState = this.popState.bind(this)

    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
    window.addEventListener("beforeunload", this.boundBeforeUnload)
    window.addEventListener("popstate", this.boundPopState, true)

    this.restoreHistoryChanges()
    this.element.setAttribute("data-ready", "")
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
    window.removeEventListener("popstate", this.boundPopState, true)
    window.clearTimeout(this.pendingNavigationReset)
    this.element.removeAttribute("data-ready")
  }

  markDirty() {
    const hasUnsavedChanges = this.hasUnsavedChanges()
    this.dirtyStatusTarget.textContent = hasUnsavedChanges ? "Unsaved changes" : "Saved"
    if (!hasUnsavedChanges) this.clearHistoryRestoration()

    if (!this.previewPanelTarget.classList.contains("hidden")) {
      this.refreshPreview()
    }
  }

  submitStart() {
    this.clearHistoryRestoration()
    this.submitting = true
  }

  submitEnd(event) {
    if (!event.detail.success) this.submitting = false
  }

  confirmNavigation(event) {
    const link = event.target.closest("a[href]")
    if (!link || link.target === "_blank" || link.hasAttribute("download")) return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (!this.hasUnsavedChanges() || this.submitting) {
      this.clearHistoryRestoration()
      return
    }

    if (window.confirm("You have unsaved strategy changes. Leave without saving?")) {
      this.clearHistoryRestoration()
      this.pendingNavigationUrl = link.href
      window.clearTimeout(this.pendingNavigationReset)
      this.pendingNavigationReset = window.setTimeout(() => {
        this.pendingNavigationUrl = null
      }, 0)
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  confirmFormNavigation(event) {
    if (event.target === this.formTarget) return
    if (!this.hasUnsavedChanges() || this.submitting) {
      this.clearHistoryRestoration()
      return
    }

    if (window.confirm("You have unsaved strategy changes. Leave without saving?")) {
      this.clearHistoryRestoration()
      this.submitting = true
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  beforeVisit(event) {
    if (this.pendingNavigationUrl === event.detail.url.toString()) {
      window.clearTimeout(this.pendingNavigationReset)
      this.pendingNavigationUrl = null
      this.clearHistoryRestoration()
      return
    }

    if (!this.hasUnsavedChanges() || this.submitting) {
      this.clearHistoryRestoration()
      return
    }

    if (window.confirm("You have unsaved strategy changes. Leave without saving?")) {
      this.clearHistoryRestoration()
      return
    }

    event.preventDefault()
  }

  beforeUnload(event) {
    if (!this.hasUnsavedChanges() || this.submitting || this.pendingNavigationUrl) {
      this.clearHistoryRestoration()
      return
    }

    event.preventDefault()
    event.returnValue = ""
  }

  popState(event) {
    const nextHistoryIndex = event.state?.turbo?.restorationIndex

    if (this.restoringHistory) {
      this.historyIndex = nextHistoryIndex
      this.restoringHistory = false
      event.stopImmediatePropagation()
      this.scheduleHistoryRestorationCleanup()
      return
    }

    if (!this.hasUnsavedChanges() || this.submitting) {
      this.clearHistoryRestoration()
      this.historyIndex = nextHistoryIndex
      return
    }

    if (window.confirm("You have unsaved strategy changes. Leave without saving?")) {
      this.clearHistoryRestoration()
      this.historyIndex = nextHistoryIndex
      return
    }

    const historyDelta = Number.isInteger(this.historyIndex) && Number.isInteger(nextHistoryIndex)
      ? this.historyIndex - nextHistoryIndex
      : 1

    this.preserveHistoryChanges()
    this.restoringHistory = true
    event.stopImmediatePropagation()
    window.history.go(historyDelta || 1)
  }

  preserveHistoryChanges() {
    const restoration = {
      url: this.editorUrl,
      originalName: this.originalName,
      originalContent: this.originalContent,
      name: this.nameTarget.value,
      content: this.textareaTarget.value
    }

    pendingHistoryRestoration = restoration
    window.setTimeout(() => {
      if (pendingHistoryRestoration === restoration) pendingHistoryRestoration = null
    }, 5000)
  }

  scheduleHistoryRestorationCleanup() {
    const restoration = pendingHistoryRestoration
    if (!restoration) return

    window.setTimeout(() => {
      if (pendingHistoryRestoration === restoration && window.location.href === this.editorUrl) {
        pendingHistoryRestoration = null
      }
    }, 1000)
  }

  clearHistoryRestoration() {
    pendingHistoryRestoration = null
  }

  restoreHistoryChanges() {
    if (!pendingHistoryRestoration || pendingHistoryRestoration.url !== window.location.href) return

    const restoration = pendingHistoryRestoration
    this.clearHistoryRestoration()
    this.originalName = restoration.originalName
    this.originalContent = restoration.originalContent
    this.nameTarget.value = restoration.name
    this.textareaTarget.value = restoration.content
    this.markDirty()
  }

  showEdit() {
    this.editPanelTarget.classList.remove("hidden")
    this.previewPanelTarget.classList.add("hidden")
    this.setActiveButton(this.editButtonTarget, true)
    this.setActiveButton(this.previewButtonTarget, false)
  }

  showPreview() {
    this.editPanelTarget.classList.add("hidden")
    this.previewPanelTarget.classList.remove("hidden")
    this.setActiveButton(this.editButtonTarget, false)
    this.setActiveButton(this.previewButtonTarget, true)
    this.refreshPreview()
  }

  refreshPreview() {
    const content = this.textareaTarget.value
    if (content === this.previewContent) return

    this.previewContent = content
    this.previewContentTarget.innerHTML = '<p class="text-sm text-[var(--color-kova-muted)]">Rendering preview...</p>'

    const requestId = ++this.previewRequest
    fetch(this.previewUrlValue, {
      method: "POST",
      headers: {
        "Accept": "text/html",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ content })
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Preview failed with ${response.status}`)
        return response.text()
      })
      .then((html) => {
        if (requestId === this.previewRequest) {
          this.previewContentTarget.innerHTML = html
        }
      })
      .catch(() => {
        if (requestId === this.previewRequest) {
          this.previewContentTarget.innerHTML = '<p class="text-sm text-red-400">Preview could not be rendered.</p>'
        }
      })
  }

  setActiveButton(button, active) {
    button.setAttribute("aria-pressed", active ? "true" : "false")
    button.classList.toggle("border-white/20", active)
    button.classList.toggle("bg-white/15", active)
    button.classList.toggle("text-white", active)
    button.classList.toggle("border-transparent", !active)
    button.classList.toggle("text-[var(--color-kova-muted)]", !active)
  }

  hasUnsavedChanges() {
    return this.nameTarget.value !== this.originalName || this.textareaTarget.value !== this.originalContent
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
